/**
 * A gestão de organizações (#767, fase 3 da #765): a lista de todas as
 * organizações, o detalhe de uma, o histórico dela e a edição do contrato e do
 * cadastro. Só o Super Admin com a concessão em vigor chama estas rotas.
 *
 * O cadastro da empresa (razão social, CNPJ, segmento) e o contato financeiro
 * nascem com a criação da organização (#769); a de antes fica com eles nulos
 * até alguém preencher no detalhe. O armazenamento em uso vem da medição de hora
 * em hora (#716): os documentos pelo R2 e os anexos das conversas pelo banco.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { DinheiroDoPlanoSchema } from './planos.js';
import { DataSchema, InstanteSchema } from './valores.js';

/** O maior número de usuários ou de GB que o banco guarda (`integer`). */
const MAIOR_INTEIRO = 2_147_483_647;

/**
 * Ativa; em criação (a criação não terminou); suspensa; encerrada (o cliente
 * saiu, e reabrir desfaz).
 */
export const SituacaoDaOrganizacaoSchema = z.enum(['ativa', 'em_criacao', 'suspensa', 'encerrada']);

export type SituacaoDaOrganizacao = z.infer<typeof SituacaoDaOrganizacaoSchema>;

/** A parte onde a criação parou, para quem vai retomá-la. */
export const ParteDaCriacaoSchema = z.enum(['acesso', 'cadastro', 'atendimento', 'administrador']);

export type ParteDaCriacao = z.infer<typeof ParteDaCriacaoSchema>;

/* ── O cadastro da empresa (#769) ────────────────────────────────────────── */

/**
 * Os segmentos, os mesmos da venda online (#564): decidem o funil sugerido e
 * as integrações oferecidas. A lista é do contrato, e não da tela: a criação,
 * o detalhe e a venda online leem a mesma, e a API recusa o que não está nela.
 */
export const SegmentoDaOrganizacaoSchema = z.enum(['veiculos', 'saude_e_estetica', 'outros']);

export type SegmentoDaOrganizacao = z.infer<typeof SegmentoDaOrganizacaoSchema>;

/** Cada segmento com o nome que a tela mostra, na ordem da escolha. */
export const SEGMENTOS_DA_ORGANIZACAO: readonly {
  readonly valor: SegmentoDaOrganizacao;
  readonly nome: string;
}[] = [
  { valor: 'veiculos', nome: 'Veículos' },
  { valor: 'saude_e_estetica', nome: 'Saúde e estética' },
  { valor: 'outros', nome: 'Outros' },
];

/**
 * O CNPJ sem a máscara: 12 letras maiúsculas ou números e os 2 dígitos
 * verificadores. As letras são do CNPJ alfanumérico, que a Receita dá às
 * inscrições novas desde julho de 2026 (IN RFB 2.229/2024); o CNPJ só de
 * números continua valendo. A conta dos dígitos verificadores é da API, que é
 * quem diz também se outra organização já o usa.
 */
export const CnpjSchema = z
  .string()
  .regex(
    /^[0-9A-Z]{12}[0-9]{2}$/,
    'O CNPJ vai sem a máscara: 12 letras maiúsculas ou números e os 2 dígitos verificadores.',
  );

/**
 * O telefone como a pessoa digitou. A API confere e grava no formato
 * internacional ("+5511987654321"), e é nele que a resposta o devolve.
 */
export const TelefoneInformadoSchema = z.string().trim().min(8).max(25);

/** A razão social, o CNPJ e o segmento, como a criação os manda. */
export const CadastroInformadoSchema = z
  .object({
    razao_social: z.string().trim().min(2).max(160),
    cnpj: CnpjSchema,
    segmento: SegmentoDaOrganizacaoSchema,
  })
  .strict();

export type CadastroInformado = z.infer<typeof CadastroInformadoSchema>;

/**
 * Quem a equipe procura para a cobrança. Não recebe e-mail do sistema: a
 * cobrança é fora dele (decisão de 26/10/2026). Dado pessoal, com base no
 * legítimo interesse: sai na purga, 60 dias depois da exclusão da organização,
 * ou antes, quando a pessoa pede (a edição do cadastro aceita apagá-lo).
 */
export const ContatoFinanceiroInformadoSchema = z
  .object({
    nome: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(254),
    telefone: TelefoneInformadoSchema,
  })
  .strict();

export type ContatoFinanceiroInformado = z.infer<typeof ContatoFinanceiroInformadoSchema>;

export const ContatoFinanceiroSchema = z
  .object({
    nome: z.string(),
    email: z.string(),
    /** No formato internacional: "+5511987654321". */
    telefone: z.string(),
  })
  .strict();

export type ContatoFinanceiro = z.infer<typeof ContatoFinanceiroSchema>;

/** Nulos na organização criada antes de a criação pedir o cadastro. */
export const CadastroDaOrganizacaoSchema = z
  .object({
    razao_social: z.string().nullable(),
    /** Sem a máscara (a tela a põe), com as letras do CNPJ alfanumérico. */
    cnpj: z.string().nullable(),
    segmento: SegmentoDaOrganizacaoSchema.nullable(),
  })
  .strict();

export type CadastroDaOrganizacao = z.infer<typeof CadastroDaOrganizacaoSchema>;

export const OrganizacaoNaGestaoSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    /** O identificador do endereço do login: "rota-sul". */
    identificador: z.string(),
    /** Sem a máscara; nulo na organização criada antes de a criação pedir o cadastro. */
    cnpj: z.string().nullable(),
    situacao: SituacaoDaOrganizacaoSchema,
    /** Só em criação, quando se sabe onde ela parou. */
    parou_em: ParteDaCriacaoSchema.nullable(),
    /** Quando a organização foi criada. */
    desde: InstanteSchema,
    /** O nome do plano do contrato; nulo enquanto a organização não tem contrato. */
    plano: z.string().nullable(),
    usuarios: z
      .object({
        /** Vínculos ativos e convites pendentes, sem as contas técnicas. */
        em_uso: z.number().int(),
        /** Nulo enquanto a organização não tem contrato. */
        contratados: z.number().int().nullable(),
      })
      .strict(),
    /** Nulo enquanto a organização não tem contrato. */
    armazenamento_contratado_gb: z.number().int().nullable(),
    /**
     * O armazenamento que conta na cota, em bytes (#716); os anexos de uma
     * conta do Atendimento dividida ficam de fora (#1161). Nulo enquanto a
     * organização não foi medida.
     */
    armazenamento_em_uso_bytes: z.number().int().nullable(),
  })
  .strict();

export type OrganizacaoNaGestao = z.infer<typeof OrganizacaoNaGestaoSchema>;

export const OrganizacoesNaGestaoSchema = z
  .object({
    /** As que a busca e o filtro de situação encontraram, por nome. */
    itens: z.array(OrganizacaoNaGestaoSchema),
    /** Quantas organizações o CliqCRM tem, sem busca nem filtro. */
    total: z.number().int(),
    /** Quantas a busca encontrou, em todas as situações. */
    encontradas: z.number().int(),
    /** Quantas a busca encontrou em cada situação. */
    por_situacao: z
      .object({
        ativa: z.number().int(),
        em_criacao: z.number().int(),
        suspensa: z.number().int(),
        encerrada: z.number().int(),
      })
      .strict(),
  })
  .strict();

export type OrganizacoesNaGestao = z.infer<typeof OrganizacoesNaGestaoSchema>;

/** Os valores DESTE cliente, copiados do plano no dia da contratação. */
export const ContratoDaOrganizacaoSchema = z
  .object({
    plano_id: IdSchema,
    inicio: DataSchema,
    /** Os preços são nulos no contrato que veio do plano legado, sem preço definido. */
    mensalidade: DinheiroDoPlanoSchema.nullable(),
    usuarios_contratados: z.number().int(),
    usuario_adicional: DinheiroDoPlanoSchema.nullable(),
    armazenamento_gb: z.number().int(),
    gb_adicional: DinheiroDoPlanoSchema.nullable(),
  })
  .strict();

export type ContratoDaOrganizacao = z.infer<typeof ContratoDaOrganizacaoSchema>;

export const OrganizacaoDetalhadaSchema = z
  .object({
    id: IdSchema,
    nome: z.string(),
    identificador: z.string(),
    situacao: SituacaoDaOrganizacaoSchema,
    parou_em: ParteDaCriacaoSchema.nullable(),
    /**
     * A exclusão de vez, quando houve. `em` é a primeira tentativa; o que o
     * CliqCRM guarda dela sai até `purga_ate` (a retenção de 60 dias; por
     * enquanto, a purga é manual). Não reabre. `incompleta`: alguma parte não
     * terminou (`partes_pendentes`), ou a exclusão começou e não chegou ao fim,
     * e excluir de novo tenta o que falta. `pendencias`: o que ficou para
     * alguém fazer depois, como cada tentativa gravou. `conta_do_atendimento_ficou`:
     * a conta do atendimento ficou de propósito (compartilhada com outra
     * organização, ou sem prova de que é desta), não conta como parte pendente,
     * e o que fazer com ela está nas pendências.
     */
    exclusao: z
      .object({
        em: InstanteSchema,
        purga_ate: InstanteSchema,
        incompleta: z.boolean(),
        partes_pendentes: z.array(z.enum(['acesso', 'atendimento', 'conexoes'])),
        pendencias: z.array(z.string()),
        conta_do_atendimento_ficou: z.boolean(),
      })
      .strict()
      .nullable(),
    /** A organização da operação da Cliqfy: não se encerra nem se exclui pela gestão. */
    da_operacao: z.boolean(),
    desde: InstanteSchema,
    /** O fuso da organização, no nome IANA: "America/Sao_Paulo". */
    fuso: z.string(),
    cadastro: CadastroDaOrganizacaoSchema,
    /**
     * Quem a equipe procura para a cobrança, guardado com o contrato. Nulo sem
     * contrato, sem contato informado, e na excluída: a gestão dela acabou.
     */
    contato_financeiro: ContatoFinanceiroSchema.nullable(),
    /**
     * Na excluída, o contato não aparece, mas fica guardado até a purga:
     * verdadeiro enquanto ele existe, para a tela oferecer apagá-lo a pedido.
     * Falso fora da excluída, onde o contato aparece em `contato_financeiro`.
     */
    contato_financeiro_ate_a_purga: z.boolean(),
    /**
     * Na organização em criação, até quando o pedido da criação fica guardado
     * para o "Retomar" (no máximo 30 dias, ADR-0009). Nulo sem pedido guardado,
     * ou fora da criação: aí a criação se refaz pelo formulário.
     */
    pedido_guardado_ate: InstanteSchema.nullable(),
    /** O plano do contrato, com os preços padrão dele, para a tela dizer o que é personalizado. */
    plano: z
      .object({
        id: IdSchema,
        nome: z.string(),
        ativo: z.boolean(),
        usuario_adicional: DinheiroDoPlanoSchema.nullable(),
        gb_adicional: DinheiroDoPlanoSchema.nullable(),
      })
      .strict()
      .nullable(),
    contrato: ContratoDaOrganizacaoSchema.nullable(),
    uso: z
      .object({
        /** Vínculos ativos e convites pendentes, sem as contas técnicas. */
        usuarios_em_uso: z.number().int(),
        /**
         * O armazenamento que conta na cota, em bytes (#716); os anexos de fora
         * vêm em `armazenamento_fora_da_cota_bytes`. Nulo enquanto a organização
         * não foi medida.
         */
        armazenamento_em_uso_bytes: z.number().int().nullable(),
        /** A medição mais antiga entre os tipos de arquivo; nula enquanto não foi medida. */
        armazenamento_medido_em: InstanteSchema.nullable(),
        /**
         * Os anexos das conversas que aparecem no uso mas não entram na cota, em
         * bytes: os da organização que divide a conta do Atendimento com outra
         * (decisão de 28/10/2026). Zero quando nada fica de fora.
         */
        armazenamento_fora_da_cota_bytes: z.number().int(),
        /**
         * A conta do Atendimento desta organização é ligada também a outra, pela
         * última medição: as duas recebem as conversas e os contatos uma da
         * outra, o que a decisão de 19/08/2026 proíbe (#1161). Falsa enquanto a
         * organização não foi medida.
         */
        conta_do_atendimento_dividida: z.boolean(),
      })
      .strict(),
    /**
     * Quem administra a organização hoje: vínculo ativo com o papel de
     * administrador. Vazia na excluída: a gestão dela acabou. O telefone é o
     * do cadastro da pessoa (#769), no formato internacional; nulo quando ela
     * não tem um.
     */
    administradores: z.array(
      z
        .object({
          id: IdSchema,
          nome: z.string(),
          email: z.string(),
          telefone: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export type OrganizacaoDetalhada = z.infer<typeof OrganizacaoDetalhadaSchema>;

/* ── O histórico ─────────────────────────────────────────────────────────── */

const DinheiroOuNada = DinheiroDoPlanoSchema.nullable();

/** Uma mudança no contrato, com o valor de antes e o de depois. */
export const MudancaNoContratoDaOrganizacaoSchema = z.discriminatedUnion('campo', [
  z
    .object({
      campo: z.literal('plano'),
      /** O nome do plano; nulo se o plano não está mais no catálogo. */
      antes: z.string().nullable(),
      depois: z.string().nullable(),
    })
    .strict(),
  z.object({ campo: z.literal('inicio'), antes: DataSchema, depois: DataSchema }).strict(),
  z
    .object({ campo: z.literal('mensalidade'), antes: DinheiroOuNada, depois: DinheiroOuNada })
    .strict(),
  z
    .object({
      campo: z.literal('usuarios_contratados'),
      antes: z.number().int(),
      depois: z.number().int(),
    })
    .strict(),
  z
    .object({
      campo: z.literal('usuario_adicional'),
      antes: DinheiroOuNada,
      depois: DinheiroOuNada,
    })
    .strict(),
  z
    .object({
      campo: z.literal('armazenamento_gb'),
      antes: z.number().int(),
      depois: z.number().int(),
    })
    .strict(),
  z
    .object({ campo: z.literal('gb_adicional'), antes: DinheiroOuNada, depois: DinheiroOuNada })
    .strict(),
]);

export type MudancaNoContratoDaOrganizacao = z.infer<typeof MudancaNoContratoDaOrganizacaoSchema>;

/**
 * Uma mudança no cadastro. A razão social, o CNPJ, o segmento e o fuso vão
 * com antes e depois. O contato financeiro é dado pessoal e não vai para a
 * trilha: o histórico diz só se ele foi incluído, trocado ou removido.
 */
export const MudancaNoCadastroDaOrganizacaoSchema = z.discriminatedUnion('campo', [
  z
    .object({
      campo: z.literal('razao_social'),
      antes: z.string().nullable(),
      depois: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      campo: z.literal('cnpj'),
      antes: z.string().nullable(),
      depois: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      campo: z.literal('segmento'),
      antes: SegmentoDaOrganizacaoSchema.nullable(),
      depois: SegmentoDaOrganizacaoSchema.nullable(),
    })
    .strict(),
  /** O fuso, no nome IANA: a criação retomada que o troca deixa a troca aqui. */
  z
    .object({
      campo: z.literal('fuso'),
      antes: z.string().nullable(),
      depois: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      campo: z.literal('contato_financeiro'),
      acao: z.enum(['incluido', 'trocado', 'removido']),
    })
    .strict(),
]);

export type MudancaNoCadastroDaOrganizacao = z.infer<typeof MudancaNoCadastroDaOrganizacaoSchema>;

export const EventoDaOrganizacaoSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('criada') }).strict(),
  z
    .object({
      tipo: z.literal('contrato_criado'),
      /** O nome do plano; nulo se o plano não está mais no catálogo. */
      plano: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      tipo: z.literal('contrato_alterado'),
      mudancas: z.array(MudancaNoContratoDaOrganizacaoSchema),
    })
    .strict(),
  z
    .object({
      tipo: z.literal('cadastro_alterado'),
      mudancas: z.array(MudancaNoCadastroDaOrganizacaoSchema),
    })
    .strict(),
  z.object({ tipo: z.literal('encerrada') }).strict(),
  z.object({ tipo: z.literal('reaberta') }).strict(),
  /** Excluída de vez: o acesso e o atendimento dela foram apagados. */
  z.object({ tipo: z.literal('excluida') }).strict(),
  /**
   * O administrador da organização contratou 1 usuário adicional, pelo convite
   * ou pela reativação (#768). Quem aceitou é o `por` da entrada, e quando, o
   * `quando`; aqui vão os usuários contratados e o valor que a pessoa viu.
   */
  z
    .object({
      tipo: z.literal('usuario_adicional_contratado'),
      usuarios_contratados: z
        .object({ antes: z.number().int(), depois: z.number().int() })
        .strict(),
      usuario_adicional: DinheiroDoPlanoSchema,
    })
    .strict(),
  /**
   * O administrador da organização contratou mais um bloco de armazenamento,
   * pela tela de Pessoas (#718). Quem aceitou é o `por` da entrada, e quando,
   * o `quando`; aqui vão o armazenamento contratado e o valor do GB adicional.
   */
  z
    .object({
      tipo: z.literal('armazenamento_adicional_contratado'),
      armazenamento_gb: z.object({ antes: z.number().int(), depois: z.number().int() }).strict(),
      gb_adicional: DinheiroDoPlanoSchema,
    })
    .strict(),
]);

export type EventoDaOrganizacao = z.infer<typeof EventoDaOrganizacaoSchema>;

export const EntradaDoHistoricoDaOrganizacaoSchema = z
  .object({
    id: IdSchema,
    quando: InstanteSchema,
    /** O nome de quem fez; nulo quando foi o sistema. */
    por: z.string().nullable(),
    evento: EventoDaOrganizacaoSchema,
  })
  .strict();

export type EntradaDoHistoricoDaOrganizacao = z.infer<typeof EntradaDoHistoricoDaOrganizacaoSchema>;

export const HistoricoDaOrganizacaoSchema = z
  .object({
    /** Da mais nova para a mais antiga. */
    itens: z.array(EntradaDoHistoricoDaOrganizacaoSchema),
  })
  .strict();

export type HistoricoDaOrganizacao = z.infer<typeof HistoricoDaOrganizacaoSchema>;

/* ── A edição do contrato ────────────────────────────────────────────────── */

/** O dinheiro que a tela manda: sempre em reais, e nunca negativo. */
export const DinheiroInformadoSchema = z
  .object({ quantia_micros: z.number().int().nonnegative(), moeda: z.literal('BRL') })
  .strict();

/**
 * Só o que vier muda, e nenhum valor volta a nulo. Trocar o plano não recopia
 * os valores: o que o cliente paga só muda quando alguém muda o valor.
 */
export const AlterarContratoDaOrganizacaoSchema = z
  .object({
    plano_id: IdSchema.optional(),
    inicio: DataSchema.optional(),
    mensalidade: DinheiroInformadoSchema.optional(),
    usuarios_contratados: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    usuario_adicional: DinheiroInformadoSchema.optional(),
    armazenamento_gb: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    gb_adicional: DinheiroInformadoSchema.optional(),
  })
  .strict()
  .refine((mudanca) => Object.keys(mudanca).length > 0, {
    message: 'Diga o que muda no contrato.',
  });

export type AlterarContratoDaOrganizacao = z.infer<typeof AlterarContratoDaOrganizacaoSchema>;

export const ContratoDaOrganizacaoAlteradoSchema = z
  .object({
    contrato: ContratoDaOrganizacaoSchema,
    /** A entrada que a mudança deixou no histórico; nula quando nada mudou. */
    entrada_no_historico: IdSchema.nullable(),
  })
  .strict();

export type ContratoDaOrganizacaoAlterado = z.infer<typeof ContratoDaOrganizacaoAlteradoSchema>;

/* ── A edição do cadastro ────────────────────────────────────────────────── */

/**
 * Só o que vier muda. A razão social, o CNPJ e o segmento não voltam a nulo. O
 * contato financeiro vai inteiro (nome, e-mail e telefone, de uma pessoa só),
 * ou nulo, que o apaga: é o caminho de quem pede para sair do cadastro.
 */
export const AlterarCadastroDaOrganizacaoSchema = z
  .object({
    razao_social: CadastroInformadoSchema.shape.razao_social.optional(),
    cnpj: CnpjSchema.optional(),
    segmento: SegmentoDaOrganizacaoSchema.optional(),
    contato_financeiro: ContatoFinanceiroInformadoSchema.nullable().optional(),
  })
  .strict()
  .refine((mudanca) => Object.keys(mudanca).length > 0, {
    message: 'Diga o que muda no cadastro.',
  });

export type AlterarCadastroDaOrganizacao = z.infer<typeof AlterarCadastroDaOrganizacaoSchema>;

/**
 * Retomar a criação que parou no meio, com o pedido que ela guardou (#769). O
 * corpo é vazio: o Retomar não responde à pergunta da conta que já existe
 * (decisão de 28/10/2026). Com ela, a criação se refaz pelo formulário de
 * "Nova organização", onde quem cria confirma.
 */
export const RetomarCriacaoDaOrganizacaoSchema = z.object({}).strict();

export type RetomarCriacaoDaOrganizacao = z.infer<typeof RetomarCriacaoDaOrganizacaoSchema>;

export const CadastroDaOrganizacaoAlteradoSchema = z
  .object({
    cadastro: CadastroDaOrganizacaoSchema,
    /**
     * Só quando o pedido trouxe o contato: o que ficou gravado, ou nulo se ele
     * foi apagado. A resposta não devolve dado pessoal que não foi pedido.
     */
    contato_financeiro: ContatoFinanceiroSchema.nullable().optional(),
    /** A entrada que a mudança deixou no histórico; nula quando nada mudou. */
    entrada_no_historico: IdSchema.nullable(),
  })
  .strict();

export type CadastroDaOrganizacaoAlterado = z.infer<typeof CadastroDaOrganizacaoAlteradoSchema>;

/* ── A contratação na criação (#769) ─────────────────────────────────────── */

/**
 * O contrato que a organização nova ganha: o plano do catálogo e o início, com
 * os valores deste cliente por cima. O que não vier fica como no plano.
 */
export const ContratacaoDaOrganizacaoSchema = z
  .object({
    plano_id: IdSchema,
    inicio: DataSchema,
    usuarios_contratados: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    usuario_adicional: DinheiroInformadoSchema.optional(),
    armazenamento_gb: z.number().int().min(1).max(MAIOR_INTEIRO).optional(),
    gb_adicional: DinheiroInformadoSchema.optional(),
  })
  .strict();

export type ContratacaoDaOrganizacao = z.infer<typeof ContratacaoDaOrganizacaoSchema>;
